import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import { useBlock } from '../../../services/entities/hooks/useBlock';
import { UnitsEnum } from '../../../services/entities/impl/blfApi/apiConstants';
import {
  selectColumnsExistInDbData,
  selectScanDataTableExists,
} from '../../duckDB/duckDBSlice';
import { DuckQueryReturnOneType } from '../../duckDB/hooks/useDuckQueryScanData';
import useMeasureAcres from '../../map/features/acreMeasure/hooks/useMeasureAcres';
import { selectCurrBlockID } from '../../select/selectSlice';
import { useUnits } from '../../settings/hooks/useUnits';
import { ScanDataEnum } from '../scanDataDefinitions';
import { useScanDataQuery } from './useScanDataQuery';

// This structure will be returned from duck db
interface BlueberryQueryData {
  sum_distance_traveled: number | null;
}

export const useCalculateCoveredPercentOfBlock = ({
  skip,
}: {
  skip?: boolean;
}): DuckQueryReturnOneType<number> => {
  const scanDataTableExists = useAppSelector(selectScanDataTableExists);
  const columnsExistInDbData = useAppSelector(selectColumnsExistInDbData);
  const currBlockID = useAppSelector(selectCurrBlockID);
  const { block: currBlock, isBlockLoading } = useBlock(currBlockID);
  const acresResult = useMeasureAcres();
  const units = useUnits();

  const blockArea = useMemo(() => {
    return units === UnitsEnum.imperial
      ? acresResult.calculatedAcres
      : acresResult.calculatedHectares;
  }, [acresResult, units]);

  const rowSpacing = useMemo(() => {
    if (!currBlock?.rowSpacing) return null;

    return units === UnitsEnum.imperial
      ? currBlock?.rowSpacing || 0
      : (currBlock?.rowSpacing || 0) * 0.3048;
  }, [currBlock, units]);

  const cols = useMemo(() => {
    return [`SUM(${ScanDataEnum.traveled_distance}) as sum_distance_traveled`];
  }, [columnsExistInDbData?.data]);

  const blueberryQueryResult = useScanDataQuery<BlueberryQueryData>({
    cols,
    skip:
      skip ||
      !scanDataTableExists ||
      !columnsExistInDbData?.data?.[ScanDataEnum.traveled_distance] ||
      isBlockLoading,
    useUnitsTransformSubstringMatching: true,
  });

  const totalAreaCovered = useMemo(() => {
    if (!rowSpacing || !blueberryQueryResult.data || !blockArea) {
      return null;
    }

    const coveredSpace = blueberryQueryResult.data.reduce((acc, el) => {
      return acc + (el.sum_distance_traveled || 0) * rowSpacing;
    }, 0);

    const convertedSpace =
      units === UnitsEnum.imperial
        ? coveredSpace / 43650 // square feets to acres
        : coveredSpace / 10000; // square meters to hectares

    return (convertedSpace / blockArea) * 100;
  }, [blueberryQueryResult, blockArea, rowSpacing]);

  if (!columnsExistInDbData?.data?.[ScanDataEnum.traveled_distance]) {
    return {
      ...blueberryQueryResult,
      data: null,
      error: new Error('Traveled distance is not provided for this scan'),
    };
  }

  if (!currBlock?.rowSpacing) {
    return {
      ...blueberryQueryResult,
      data: null,
      error: new Error('Row spacing is not provided for this block'),
    };
  }

  if (!blockArea) {
    return {
      ...blueberryQueryResult,
      data: null,
      error: new Error('Unable to calculate block area'),
    };
  }

  return {
    ...blueberryQueryResult,
    data: totalAreaCovered,
  };
};
